import { MouseEvent, useMemo, useState } from "react"
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faCalendarDay,
    faClock,
    faDollarSign,
    faLocationDot,
    faTruck,
    faUser,
    faUserGroup,
} from "@fortawesome/pro-regular-svg-icons"
import { faEllipsisV, faMessageCaptions, faMugSaucer } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveCompanyId, selectEffectiveUserId } from "../../../../../../context/contextSelectors"
import { useMoney } from "../../../../../../currency/useMoney"
import { toDineroOrUndefined } from "../../../../../../currency/currencyLib"
import { OrderChatDrawer } from "../../../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../../../shared/components/orderChat/orderChatTypes"
import { useClock } from "../../../../../../../shared/hooks/useClock"
import { SimpleStatusChip } from "../../../../../../merchant/merchant/production/meetingService/components/StatusMenu"
import {
    MeetingTimeLeftTag,
    MeetingTimeLeftVariant,
} from "../../../../../../../shared/components/meetingTimeLeftTag/MeetingTimeLeftTag"
import { UserMeetingsReceipt } from "../../../../MeetingsReceipt/UserMeetingsReceipt"
import { MeetingRowOrder, MenuMeeting } from "../../../weekMenuTypes"

import { MeetingCardMenu } from "./MeetingCardMenu"
import { MeetingMetaItem } from "./MeetingMetaItem"
import { useMeetingTiming } from "./useMeetingTiming"

const useStyles = makeStyles()((theme) => ({
    row: {
        display: "flex",
        alignItems: "stretch",
        padding: ".75rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    timeBlock: {
        display: "flex",
        flexDirection: "column",
    },
    timeStart: {
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        fontWeight: 500,
    },
    timeEnd: {
        color: theme.palette.tailwind.slate[500],
        fontSize: ".875rem",
        fontWeight: 400,
    },
    separator: {
        width: ".1875rem",
        background: theme.palette.tailwind.blue[200],
        margin: "0 1rem",
        borderRadius: ".09375rem",
    },
    content: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
    },
    section: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
    },
    sectionDivider: {
        margin: ".375rem 0",
        borderColor: theme.palette.tailwind.slate[200],
        borderStyle: "dashed",
    },
    titleLine: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".5rem",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    title: {
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        fontWeight: 500,
    },
    titleIconWrap: {
        width: "1rem",
        height: "1rem",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
    },
    titleIcon: {
        color: theme.palette.tailwind.slate[900],
        fontSize: "1rem",
    },
    rightControls: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    chatBtn: {
        width: "1.75rem",
        height: "1.75rem",
    },
    menuButton: {
        width: "1.25rem",
        height: "1.25rem",
    },
    menuButtonPlaceholder: {
        width: "1.25rem",
        height: "1.25rem",
    },
    chatIcon: {
        color: theme.palette.tailwind.slate[400],
        fontSize: "1rem",
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        rowGap: ".25rem",
        color: theme.palette.tailwind.slate[500],
    },
}))

type MeetingRowProps = Readonly<{
    meeting: MeetingRowOrder
    meetings: MeetingRowOrder[]
}>

const mapMeeting = (meeting: MeetingRowOrder): MenuMeeting => ({
    id: meeting.id,
    uid: meeting.uid,
    orderSummary: meeting.orderSummary,
    deliveryDetails: { time: meeting.deliveryDetails.time || meeting.meetingDetails.startDateTime || "" },
    organizers: meeting.organizers,
    meetingDetails: {
        meetingName: meeting.meetingDetails.meetingName,
        startDateTime: meeting.deliveryDetails.time || meeting.meetingDetails.startDateTime || "",
        endDateTime: meeting.meetingDetails.endDateTime || "",
    },
    messages: meeting.messages,
})

export function MeetingRow({ meeting, meetings }: MeetingRowProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const userId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [chatOpen, setChatOpen] = useState(false)
    const [receiptOpen, setReceiptOpen] = useState(false)

    // second resolution so an ended booking leaves the "in progress" state right away
    const { clock: now } = useClock({ resolution: "second" })
    const { start, end, isInProgress, hoursLeft, minutesLeftRemainder } = useMeetingTiming(meeting, now)

    const { id, messages, meetingDetails, deliveryDetails, orderSummary, amount } = meeting
    const { meetingName, location: meetingLocation } = meetingDetails
    const { location: deliveryLocation, isLocked, statusDetails, sittings } = deliveryDetails
    const { hasBooking, hasCatering, participants } = orderSummary

    const isDualSection = hasBooking && hasCatering
    const titleIcon = hasCatering && !hasBooking ? faMugSaucer : faCalendarDay
    // The API sets deliveryDetails.type for pure resource bookings as well, so a delivery is only
    // shown when the order actually has catering; otherwise the location is the booked resource.
    const deliveryType = hasCatering ? deliveryDetails.type : undefined
    const deliveryLocationName = deliveryLocation?.name
    const hasChat = !!messages && messages.length > 0
    const priceDinero = toDineroOrUndefined(amount)
    const locationParts = Array.from(
        new Set(
            [deliveryLocationName, meetingLocation]
                .map((part) => part?.trim())
                .filter((part): part is string => Boolean(part)),
        ),
    )
    const combinedLocationText = locationParts.join(" - ")

    const menuMeeting = useMemo(() => mapMeeting(meeting), [meeting])
    const allMeetingsFormatted = useMemo(() => meetings.map(mapMeeting), [meetings])

    function handleMenuOpen(event: MouseEvent<HTMLElement>) {
        setAnchorEl(event.currentTarget)
    }

    function handleMenuClose() {
        setAnchorEl(null)
    }

    function renderStatusOrMinLeft() {
        if (isInProgress) {
            return (
                <MeetingTimeLeftTag variant={MeetingTimeLeftVariant.RED}>
                    <LocalizedStrict
                        id="user-home-upcoming-meeting-min-left"
                        vars={{ hours: hoursLeft, minutes: minutesLeftRemainder }}
                    >
                        <Typography variant="body2" component="span">
                            {hoursLeft > 0 && `${hoursLeft} hr`}
                            {hoursLeft > 0 && minutesLeftRemainder > 0 && " "}
                            {minutesLeftRemainder > 0 && `${minutesLeftRemainder} min`}
                            {" left"}
                        </Typography>
                    </LocalizedStrict>
                </MeetingTimeLeftTag>
            )
        }
        if (hasCatering && statusDetails?.status) {
            return <SimpleStatusChip status={statusDetails.status} tooltipLabel={statusDetails.message} size="medium" />
        }
        return null
    }

    function renderChatButton() {
        if (!hasChat) return null
        return (
            <IconButton size="small" onClick={() => setChatOpen(true)} className={classes.chatBtn}>
                <FontAwesomeIcon icon={faMessageCaptions} className={classes.chatIcon} />
            </IconButton>
        )
    }

    function renderKebab() {
        return (
            <IconButton size="small" className={classes.menuButton} onClick={handleMenuOpen}>
                <FontAwesomeIcon icon={faEllipsisV} />
            </IconButton>
        )
    }

    const rowContent = (
        <Box className={classes.row}>
            <Box className={classes.timeBlock}>
                {start?.isValid && (
                    <Typography variant="h5" className={classes.timeStart}>
                        {dateTimeFactory.formatTime(start)}
                    </Typography>
                )}
                {end?.isValid && (
                    <Typography variant="h6" className={classes.timeEnd}>
                        {dateTimeFactory.formatTime(end)}
                    </Typography>
                )}
            </Box>

            <Box className={classes.separator} />

            <Box className={classes.content}>
                <Box className={classes.section}>
                    <Box className={classes.titleLine}>
                        <Box className={classes.titleRow}>
                            <Typography variant="h6" className={classes.title}>
                                {meetingName}
                            </Typography>
                            <Box className={classes.titleIconWrap}>
                                <FontAwesomeIcon icon={titleIcon} className={classes.titleIcon} />
                            </Box>
                        </Box>
                        <Box className={classes.rightControls}>
                            {!isDualSection && renderChatButton()}
                            {!isDualSection && renderStatusOrMinLeft()}
                            {renderKebab()}
                        </Box>
                    </Box>

                    <Box className={classes.metaRow}>
                        <MeetingMetaItem>
                            <Typography variant="h6" component="span">
                                #{id}
                            </Typography>
                        </MeetingMetaItem>
                        {deliveryType && (
                            <MeetingMetaItem icon={faTruck}>
                                <Typography variant="h6" component="span">
                                    <LocalizedEnum base="delivery-type-filter" enumValue={deliveryType}>
                                        <>{deliveryType}</>
                                    </LocalizedEnum>
                                    {combinedLocationText && `: ${combinedLocationText}`}
                                </Typography>
                            </MeetingMetaItem>
                        )}
                        {!deliveryType && combinedLocationText && (
                            <MeetingMetaItem icon={faLocationDot}>
                                <Typography variant="h6" component="span">
                                    {combinedLocationText}
                                </Typography>
                            </MeetingMetaItem>
                        )}
                        {!isDualSection && priceDinero && (
                            <MeetingMetaItem icon={faDollarSign}>
                                <Typography variant="h6" component="span">
                                    {moneyFactory.format(priceDinero)}
                                </Typography>
                            </MeetingMetaItem>
                        )}
                    </Box>
                </Box>

                {isDualSection && (
                    <>
                        <Divider className={classes.sectionDivider} />
                        <Box className={classes.section}>
                            <Box className={classes.titleLine}>
                                <Box className={classes.titleRow}>
                                    <LocalizedStrict id="user-home-upcoming-meeting-service-sub-title">
                                        <Typography variant="h6" className={classes.title}>
                                            Meeting service
                                        </Typography>
                                    </LocalizedStrict>
                                    <Box className={classes.titleIconWrap}>
                                        <FontAwesomeIcon icon={faMugSaucer} className={classes.titleIcon} />
                                    </Box>
                                </Box>
                                <Box className={classes.rightControls}>
                                    {renderChatButton()}
                                    {renderStatusOrMinLeft()}
                                    <Box className={classes.menuButtonPlaceholder} aria-hidden />
                                </Box>
                            </Box>

                            <Box className={classes.metaRow}>
                                {!!sittings && (
                                    <MeetingMetaItem icon={faUser}>
                                        <LocalizedStrict
                                            id="user-home-upcoming-meeting-service-sittings"
                                            vars={{ sittings }}
                                        >
                                            <Typography variant="h6" component="span">
                                                {sittings} seats
                                            </Typography>
                                        </LocalizedStrict>
                                    </MeetingMetaItem>
                                )}
                                {!!participants && (
                                    <MeetingMetaItem icon={faUserGroup}>
                                        <LocalizedStrict
                                            id="user-home-upcoming-meeting-service-attendees"
                                            vars={{ attendees: participants }}
                                        >
                                            <Typography variant="h6" component="span">
                                                {participants} participants
                                            </Typography>
                                        </LocalizedStrict>
                                    </MeetingMetaItem>
                                )}
                                {deliveryLocationName && deliveryType && (
                                    <MeetingMetaItem icon={faTruck}>
                                        <Typography variant="h6" component="span">
                                            <LocalizedEnum base="delivery-type-filter" enumValue={deliveryType}>
                                                <>{deliveryType}</>
                                            </LocalizedEnum>
                                        </Typography>
                                    </MeetingMetaItem>
                                )}
                                {deliveryDetails.time && (
                                    <MeetingMetaItem icon={faClock}>
                                        <Typography variant="h6" component="span">
                                            {dateTimeFactory.formatTime(dateTimeFactory.fromApi(deliveryDetails.time))}
                                        </Typography>
                                    </MeetingMetaItem>
                                )}
                                {priceDinero && (
                                    <MeetingMetaItem icon={faDollarSign}>
                                        <Typography variant="h6" component="span">
                                            {moneyFactory.format(priceDinero)}
                                        </Typography>
                                    </MeetingMetaItem>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )

    return (
        <>
            {isLocked && hasCatering ? (
                <LocalizedStrict id="meeting-service-table-lock-column-locked" attrs={{ title: true }}>
                    <Tooltip title="">{rowContent}</Tooltip>
                </LocalizedStrict>
            ) : (
                rowContent
            )}

            <MeetingCardMenu
                meeting={menuMeeting}
                anchorEl={anchorEl}
                meetings={allMeetingsFormatted}
                menuOnApi
                handleMenuClose={handleMenuClose}
                onChatOpen={() => setChatOpen(true)}
            />

            {chatOpen && (
                <OrderChatDrawer
                    open
                    onClose={() => setChatOpen(false)}
                    orderId={id}
                    orderTitle={meetingName}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={userId && companyId ? { type: UserType.USER, companyId, userId } : undefined}
                    onSubtitleClick={() => setReceiptOpen(true)}
                />
            )}

            {receiptOpen && (
                <UserMeetingsReceipt
                    open={receiptOpen}
                    id={id}
                    meetings={allMeetingsFormatted}
                    onClose={() => setReceiptOpen(false)}
                />
            )}
        </>
    )
}
